/** 元 → 分（四舍五入到分） */
export function yuanToCents(yuan: number): number {
  return Math.round(Number(yuan) * 100);
}

/**
 * 出厂价展示：按自然精度显示，最多保留 3 位小数（第 3 位为 0 时省略），至少 2 位。
 * 例：0.455→"0.455"、0.530→"0.53"、10→"10.00"。
 * 注意：仅用于「出厂价」等可能含 3 位小数的原始录入价展示；商城销售价仍按 2 位金额引擎处理。
 */
export function formatExFactoryPriceYuan(yuan: number | string | null | undefined): string {
  const num = Number(yuan ?? 0);
  if (!Number.isFinite(num)) return '0.00';
  const s3 = num.toFixed(3);
  return s3.endsWith('0') ? num.toFixed(2) : s3;
}

/** 分 → 元 */
export function centsToYuan(cents: number): number {
  return cents / 100;
}

/** 金额四舍五入到分（整数分运算，避免二进制浮点误差） */
export function roundMoney2(yuan: number): number {
  if (!Number.isFinite(yuan)) return NaN;
  return centsToYuan(yuanToCents(yuan));
}

/** 两金额相加，结果四舍五入到分 */
export function addMoney2(a: number, b: number): number {
  return centsToYuan(yuanToCents(a) + yuanToCents(b));
}

/** 金额 × 小数费率（如 0.15），结果四舍五入到分 */
export function multiplyMoneyByRate(yuan: number, rate: number): number {
  const cents = yuanToCents(yuan);
  const rateBp = Math.round(Number(rate) * 10000);
  if (!Number.isFinite(rateBp) || rateBp < 0) return centsToYuan(cents);
  return centsToYuan(Math.round((cents * rateBp) / 10000));
}

/**
 * 未税销售单价 = 出厂价 + 未税技术服务费
 * 未税技术服务费 = 出厂价 × 费率 + 预估运费单价
 */
export function computeUntaxedSaleUnitFromExFactory(
  exFactoryYuan: number,
  markupRate: number,
  estimatedFreightUnitYuan = 0,
): number {
  const exCents = yuanToCents(exFactoryYuan);
  if (exCents <= 0) return 0;
  const rateBp = Math.round(Number(markupRate) * 10000);
  const rateSafe = Number.isFinite(rateBp) && rateBp >= 0 ? rateBp : 0;
  const withRate = Math.round((exCents * (10000 + rateSafe)) / 10000);
  const freightCents = Math.max(0, yuanToCents(Number(estimatedFreightUnitYuan) || 0));
  return centsToYuan(withRate + freightCents);
}

/** 最小起订量预估运费（合计）分摊到单价 */
export function allocateMoqFreightToUnit(
  estimatedFreightMoqYuan: number,
  minOrderQty: number,
): number {
  const moq = Math.max(1, Math.floor(Number(minOrderQty) || 1));
  const total = Math.max(0, Number(estimatedFreightMoqYuan) || 0);
  return roundMoney2(total / moq);
}

/** 未税技术服务费单价 = 出厂价 × 费率 + 预估运费单价 */
export function computeServiceFeeUnitFromExFactory(
  exFactoryYuan: number,
  markupRate: number,
  estimatedFreightUnitYuan = 0,
): number {
  const ratePart = multiplyMoneyByRate(exFactoryYuan, markupRate);
  return addMoney2(ratePart, Math.max(0, Number(estimatedFreightUnitYuan) || 0));
}
